import {CheckIcon, ChevronsUpDownIcon, PlusIcon, Trash2Icon} from "lucide-react";
import {useState} from "react";

import {Button} from "@components/ui/button";
import {type VNextScenario} from "@hooks/use-vnext";

interface ScenarioSelectorProps {
    scenarios: VNextScenario[];
    selected: VNextScenario | null;
    onSelect: (scenario: VNextScenario) => void;
    onCreate?: (name: string) => void;
    onDelete?: (scenario: VNextScenario) => void;
}

export function ScenarioSelector({scenarios, selected, onSelect, onCreate, onDelete}: ScenarioSelectorProps) {
    const [open, setOpen] = useState(false);
    const [newName, setNewName] = useState("");
    const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

    if (scenarios.length <= 1 && !onCreate) return null;

    return (
        <div className="relative" data-tour="workbench-scenario-selector">
            <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 gap-1.5 text-xs font-medium"
                onClick={() => setOpen((v) => !v)}
            >
                <span className="max-w-[160px] truncate">{selected?.name ?? "Select scenario"}</span>
                <ChevronsUpDownIcon className="size-3 opacity-50" />
            </Button>

            {open && (
                <>
                    <div className="fixed inset-0 z-40" onClick={() => { setOpen(false); setConfirmDeleteId(null); }} />
                    <div className="absolute left-0 top-full z-50 mt-1 min-w-[220px] rounded-lg border border-[#E2E8F0] bg-white shadow-lg">
                        <div className="max-h-[240px] overflow-y-auto p-1">
                            {scenarios.map((s) => (
                                <div key={s.id} className="flex items-center rounded hover:bg-[#F0FDFA] transition-colors">
                                    <button
                                        type="button"
                                        className="flex flex-1 items-center gap-2 px-3 py-2 text-left text-xs"
                                        onClick={() => {
                                            onSelect(s);
                                            setOpen(false);
                                            setConfirmDeleteId(null);
                                        }}
                                    >
                                        <span className="size-3.5 shrink-0">
                                            {s.id === selected?.id && <CheckIcon className="size-3.5 text-[#209DB3]" />}
                                        </span>
                                        <span className="flex-1 truncate font-medium text-[#0F172A]">{s.name}</span>
                                        {s.is_default && (
                                            <span className="shrink-0 rounded bg-[#F0FDFA] px-1.5 py-0.5 text-[10px] font-medium text-[#209DB3]">
                                                default
                                            </span>
                                        )}
                                    </button>
                                    {onDelete && !s.is_default && (
                                        confirmDeleteId === s.id ? (
                                            <button
                                                type="button"
                                                className="shrink-0 mr-2 rounded bg-rose-100 px-2 py-1 text-[10px] font-medium text-rose-700 hover:bg-rose-200 transition-colors"
                                                onClick={() => {
                                                    onDelete(s);
                                                    setConfirmDeleteId(null);
                                                    setOpen(false);
                                                }}
                                            >
                                                Confirm
                                            </button>
                                        ) : (
                                            <button
                                                type="button"
                                                className="shrink-0 mr-2 rounded p-1 text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setConfirmDeleteId(s.id);
                                                }}
                                            >
                                                <Trash2Icon className="size-3" />
                                            </button>
                                        )
                                    )}
                                </div>
                            ))}
                        </div>
                        {onCreate && (
                            <div className="border-t border-[#E2E8F0] p-2">
                                <form
                                    className="flex gap-1.5"
                                    onSubmit={(e) => {
                                        e.preventDefault();
                                        const trimmed = newName.trim();
                                        if (!trimmed) return;
                                        onCreate(trimmed);
                                        setNewName("");
                                        setOpen(false);
                                    }}
                                >
                                    <input
                                        type="text"
                                        value={newName}
                                        onChange={(e) => setNewName(e.target.value)}
                                        placeholder="New scenario name"
                                        className="flex-1 rounded border border-[#E2E8F0] px-2 py-1 text-xs focus:border-[#209DB3] focus:outline-none"
                                    />
                                    <Button type="submit" variant="ghost" size="sm" className="h-7 w-7 p-0" disabled={!newName.trim()}>
                                        <PlusIcon className="size-3.5" />
                                    </Button>
                                </form>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
